/**
 * Contact sections for the RMA create/edit form — shipping, billing, and
 * reporting contacts, each backed by the shared ContactSelector component.
 *
 * Extracted from CreateReturnForm to keep that file under the 800-line cap.
 * Not intended for use outside of CreateReturnForm.
 *
 * Contains:
 * - CreateReturnFormContacts — renders the three ContactSelector blocks
 */

import { Box, Divider, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ContactSelector } from '@sophocles/components/contacts';

/**
 * @param {object} props
 * @param {number} props.customerId
 * @param {number} props.siteGroupId
 * @param {object} props.customer        - Immutable Map
 * @param {object} props.siteGroup       - Immutable Map
 * @param {object|null} props.selectedReportingContact
 * @param {object|null} props.selectedShippingContact
 * @param {object|null} props.selectedBillingContact
 * @param {function} props.onReportingChange
 * @param {function} props.onShippingChange
 * @param {function} props.onBillingChange
 * @param {function} props.onReportingValidation
 * @param {function} props.onShippingValidation
 * @param {function} props.onBillingValidation
 */
export default function CreateReturnFormContacts({
  customerId,
  siteGroupId,
  customer,
  siteGroup,
  selectedReportingContact,
  selectedShippingContact,
  selectedBillingContact,
  onReportingChange,
  onShippingChange,
  onBillingChange,
  onReportingValidation,
  onShippingValidation,
  onBillingValidation,
}) {
  const { t } = useTranslation();
  return (
    <>
      <Box sx={{ mt: 2, mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {t('rma.reportingContact', 'Reporting Contact')}
        </Typography>
        <ContactSelector
          value={selectedReportingContact}
          onChange={onReportingChange}
          customerId={customerId}
          siteGroupId={siteGroupId}
          customer={customer}
          siteGroup={siteGroup}
          label={t('rma.reportingContact', 'Reporting Contact')}
          showAddress={false}
          required={true}
          validationRules={{ requireName: true, requireEmailOrPhone: true }}
          onValidationChange={onReportingValidation}
          quickMode={true}
          requireCompleteAddress={false}
          requiredFields={['email', 'name']}
          primaryContext="rma"
          contextLabel={t('rma.rma', 'RMA')}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {t('rma.shippingAddress', 'Shipping Address')}
        </Typography>
        <ContactSelector
          value={selectedShippingContact}
          onChange={onShippingChange}
          customerId={customerId}
          siteGroupId={siteGroupId}
          customer={customer}
          siteGroup={siteGroup}
          label={t('rma.shippingAddress', 'Shipping Address')}
          addressType="shipping"
          showAddress={true}
          required={true}
          validationRules={{ requireName: true, requireAddress: true }}
          onValidationChange={onShippingValidation}
          showDefaultAddresses={true}
          requiredFields={['address', 'name']}
          primaryContext="rma"
          contextLabel={t('rma.rma', 'RMA')}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {t('rma.billingAddress', 'Billing Address')}
        </Typography>
        <ContactSelector
          value={selectedBillingContact}
          onChange={onBillingChange}
          customerId={customerId}
          siteGroupId={siteGroupId}
          customer={customer}
          siteGroup={siteGroup}
          label={t('rma.billingAddress', 'Billing Address')}
          addressType="billing"
          showAddress={true}
          required={true}
          validationRules={{ requireName: true, requireAddress: true }}
          onValidationChange={onBillingValidation}
          showDefaultAddresses={true}
          requiredFields={['address', 'name']}
          primaryContext="rma"
          contextLabel={t('rma.rma', 'RMA')}
        />
      </Box>
    </>
  );
}
